import React, { useState, useEffect } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../Components/Loader";
import "../../Styles/FacultyAttendance.css";

function FacultyAttendance() {
    const [facultyList, setFacultyList] = useState([]);
    const [attendance, setAttendance] = useState({});
    const [selectedSubjects, setSelectedSubjects] = useState({});
    const [selectedClasses, setSelectedClasses] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");


    useEffect(() => {
        const fetchFacultyList = async () => {
            try {
                const token = localStorage.getItem("authToken");
                if (!token) return;

                const response = await axios.get(
                    "https://api.uniums.com/api/admin/faculty-attendance-details",
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (response.data) {
                    const fetchedFaculty = response.data.data;
                    const initialAttendance = {};
                    const initialSubjects = {};
                    const initialClasses = {};

                    fetchedFaculty.forEach(faculty => {
                        initialAttendance[faculty._id] = faculty.attendanceStatus || null;
                        initialSubjects[faculty._id] = faculty.subjects || [];
                        initialClasses[faculty._id] = faculty.classes || [];

                    });

                    setAttendance(initialAttendance);
                    setSelectedSubjects(initialSubjects);
                    setSelectedClasses(initialClasses);
                    setFacultyList(fetchedFaculty);
                    setLoading(false);
                } else {
                    setLoading(false);
                    setError("No data available.");
                }
            } catch (err) {
                setError("Error fetching faculty details.");
                setLoading(false);
            }
        };

        fetchFacultyList();
    }, []);


    const handleAttendanceChange = (id, status) => {
        setAttendance((prev) => ({
            ...prev,
            [id]: status,
        }));
        if (status === "Absent") {
            setSelectedSubjects((prev) => ({
                ...prev,
                [id]: [],
            }));
            setSelectedClasses((prev) => ({
                ...prev,
                [id]: [],
            }));
        }
        if (status === "Present") {
            setSelectedSubjects((prev) => ({
                ...prev,
                [id]: facultyList.find(faculty => faculty._id === id)?.subjects || [],
            }));
            setSelectedClasses((prev) => ({
                ...prev,
                [id]: facultyList.find(faculty => faculty._id === id)?.classes || [],
            }));
        }
        setIsSubmitEnabled(true);
    };



    const handleSubjectChange = (facultyId, selectedValues) => {
        setSelectedSubjects(prev => ({
            ...prev,
            [facultyId]: selectedValues,
        }));
        setIsSubmitEnabled(true);
    };

    const handleClassChange = (facultyId, selectedValues) => {
        setSelectedClasses(prev => ({
            ...prev,
            [facultyId]: selectedValues,
        }));
        setIsSubmitEnabled(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        for (const [facultyId, status] of Object.entries(attendance)) {
            if (status === "Present") {
                const faculty = facultyList.find((f) => f._id === facultyId);

                if (!selectedSubjects[facultyId] || selectedSubjects[facultyId].length === 0) {
                    toast.error(`Please select at least one subject for ${faculty?.name || "faculty"}`);
                    return;
                }
                if (!selectedClasses[facultyId] || selectedClasses[facultyId].length === 0) {
                    toast.error(`Please select at least one class for ${faculty?.name || "faculty"}`);
                    return;
                }
            }
        }

        setIsSubmitting(true);

        try {
            const token = localStorage.getItem("authToken");
            const payload = {
                attendance: Object.entries(attendance).map(([facultyId, status]) => ({
                    facultyId,
                    status,
                    subjects: selectedSubjects[facultyId] || [],
                    classes: selectedClasses[facultyId] || [],
                })),
            };

            await axios.post(
                "https://api.uniums.com/api/admin/faculty-attendance",
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            toast.success("Attendance submitted successfully!");
            setIsSubmitEnabled(false);
        } catch (error) {
            toast.error("Failed to submit attendance. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };
    const filteredFacultyList = facultyList.filter(
        (faculty) =>
            faculty.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faculty.employeeCode.toLowerCase().includes(searchQuery.toLowerCase())
    );




    if (loading) return <Loader />;
    if (error) return <div>{error}</div>;

    return (
        <div className="faculty-attendance-container">
            <h1>Faculty Attendance Management</h1>
            <div className="search-bar">
                <input
                    type="text"
                    placeholder="Search by name or employee code"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-box"
                />
            </div>
            <form onSubmit={handleSubmit} className="attendance-form">
                <div className="faculty-table-container">
                    <table className="attendance-table">
                        <thead>
                            <tr>
                                <th className="stick_sl">Sl.</th>
                                <th className="sticky_f sticky_b">Faculty Name</th>
                                <th className="stick_usecode">User Code</th>
                                <th className="stick_status">Status</th>
                                <th className="stick_subjects">Subjects</th>
                                <th className="stick_classes">Classes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredFacultyList.map((faculty, index) => (
                                <tr key={faculty._id}>
                                    <td>{index + 1}</td>
                                    <td className="sticky_f sticky_w">{faculty.name}</td>
                                    <td>{faculty.employeeCode}</td>
                                    <td>
                                        <div className="radio-group">
                                            <label>
                                                <input
                                                    type="radio"
                                                    name={`attendance-${faculty._id}`}
                                                    value="Present"
                                                    checked={attendance[faculty._id] === "Present"}
                                                    onChange={() => handleAttendanceChange(faculty._id, "Present")}
                                                />
                                                Present
                                            </label>
                                            <label>
                                                <input
                                                    type="radio"
                                                    name={`attendance-${faculty._id}`}
                                                    value="Absent"
                                                    checked={attendance[faculty._id] === "Absent"}
                                                    onChange={() => handleAttendanceChange(faculty._id, "Absent")}
                                                />
                                                Absent
                                            </label>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="custom_dropdown_container">
                                            {faculty.subjects && faculty.subjects.length > 0 ? (
                                                <div className="checkbox_container">
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedSubjects[faculty._id]?.length === faculty.subjects.length}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    handleSubjectChange(faculty._id, faculty.subjects || []);
                                                                } else {
                                                                    handleSubjectChange(faculty._id, []);
                                                                }
                                                            }}
                                                            disabled={attendance[faculty._id] === "Absent"}
                                                        />
                                                        Select All
                                                    </label>
                                                    {[...new Set(faculty.subjects)].map((subject, index) => (
                                                        <label key={index}>
                                                            <input
                                                                type="checkbox"
                                                                value={subject}
                                                                checked={selectedSubjects[faculty._id]?.includes(subject)}
                                                                onChange={(e) => {
                                                                    const updatedSelection = e.target.checked
                                                                        ? [...(selectedSubjects[faculty._id] || []), subject]
                                                                        : (selectedSubjects[faculty._id] || []).filter((s) => s !== subject);
                                                                    handleSubjectChange(faculty._id, updatedSelection);
                                                                }}
                                                                disabled={attendance[faculty._id] === "Absent"}
                                                            />
                                                            {subject}
                                                        </label>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div>No subjects available</div>
                                            )}
                                        </div>
                                    </td>

                                    <td>
                                        <div className="custom_dropdown_container">
                                            {faculty.classes && faculty.classes.length > 0 ? (
                                                <div className="checkbox_container">
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedClasses[faculty._id]?.length === faculty.classes.length}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    handleClassChange(faculty._id, faculty.classes || []);
                                                                } else {
                                                                    handleClassChange(faculty._id, []);
                                                                }
                                                            }}
                                                            disabled={attendance[faculty._id] === "Absent"}
                                                        />
                                                        Select All
                                                    </label>
                                                    {[...new Set(faculty.classes)].map((cls, index) => (
                                                        <label key={index}>
                                                            <input
                                                                type="checkbox"
                                                                value={cls}
                                                                checked={selectedClasses[faculty._id]?.includes(cls)}
                                                                onChange={(e) => {
                                                                    const updatedSelection = e.target.checked
                                                                        ? [...(selectedClasses[faculty._id] || []), cls]
                                                                        : (selectedClasses[faculty._id] || []).filter((c) => c !== cls);
                                                                    handleClassChange(faculty._id, updatedSelection);
                                                                }}
                                                                disabled={attendance[faculty._id] === "Absent"}
                                                            />
                                                            {cls}
                                                        </label>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div>No classes available</div>
                                            )}
                                        </div>
                                    </td>

                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <button
                    type="submit"
                    className="btn-submit btn_submit_attendance"
                    disabled={!isSubmitEnabled || isSubmitting}
                >
                    {isSubmitting ? "Submitting..." : "Submit Attendance"}
                </button>
            </form>
            <ToastContainer position="top-right" autoClose={3000} />
        </div>
    );
}

export default FacultyAttendance;
